// src/components/users/UserModal.jsx
import React, { useEffect, useState } from "react";
import authAxios from "../../api/authAxios";

export default function UserModal({
  open,
  onClose,
  mode = "add",
  initialUser = null,
  organizations = [],
  currentUser = null,
  organization = null,
  onSubmit,
}) {
  /* ---------------------------------------------------
   * Form state
   * --------------------------------------------------- */
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    org_role: "org_user",
    organization: organization?.id || "",
  });

  /* ---------------------------------------------------
   * Site access state
   * --------------------------------------------------- */
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [defaultSiteId, setDefaultSiteId] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);

  /* ---------------------------------------------------
   * Permissions
   * --------------------------------------------------- */
  const isPlatformAdmin = currentUser?.global_role === "platform_admin";
  const isOrgAdmin =
    isPlatformAdmin ||
    currentUser?.memberships?.some((m) => m.role === "org_admin");
  const isOrgUser =
    !isPlatformAdmin &&
    currentUser?.memberships?.some((m) => m.role === "org_user");



  /* ---------------------------------------------------
   * Initialize form (add / edit)
   * --------------------------------------------------- */
  useEffect(() => {
    if (!open) return;

    if (initialUser) {
      setForm({
        name: initialUser.name || "",
        email: initialUser.email || "",
        password: "",
	org_role: initialUser.org_role_read || "org_user",
        organization:
          initialUser.memberships?.[0]?.organization?.id ||
          organization?.id ||
          "",
      });
    } else {
      setForm({
        name: "",
        email: "",
        password: "",
        org_role: "org_user",
        organization: organization?.id || "",
      });
      setSelectedSites([]);
      setDefaultSiteId(null);
    }
  }, [open, initialUser, organization]);

  /* ---------------------------------------------------
   * Load sites for selected organization
   * --------------------------------------------------- */
  useEffect(() => {
    if (!form.organization || !isOrgAdmin) {
      setSites([]);
      return;
    }

    setLoadingSites(true);

    authAxios
      .get(`/organizations/${form.organization}/assignable-sites/`)
      .then((res) => {
        setSites(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Failed to load sites:", err);
        setSites([]);
      })
      .finally(() => setLoadingSites(false));
  }, [form.organization, isOrgAdmin]);



  /* ---------------------------------------------------
   * Hydrate user in edit mode (fetch org-scoped fields)
   * --------------------------------------------------- */
  useEffect(() => {
    if (!open) return;
    if (!initialUser?.id) return;

    // We MUST know which org context we're editing in
    const orgId =
      Number(form.organization) ||
      Number(initialUser?.memberships?.[0]?.organization?.id) ||
      Number(organization?.id);

    if (!orgId) return;

    // Fetch full user shape including:
    // - org_role_read
    // - assigned_site_ids
    // - default_site_id
    authAxios
      .get(`/users/${initialUser.id}/?organization=${orgId}`)
      .then((res) => {
        const u = res.data;

        // ✅ update org role in the form
        setForm((prev) => ({
          ...prev,
          org_role: u.org_role_read || prev.org_role || "org_user",
          organization: String(orgId),
          name: u.name || prev.name,
          email: u.email || prev.email,
        }));

        // ✅ update selected sites + default site
        setSelectedSites(Array.isArray(u.assigned_site_ids) ? u.assigned_site_ids : []);
        setDefaultSiteId(u.default_site_id || null);
      })
      .catch((err) => {
        console.error("❌ Failed to hydrate edit user:", err?.response?.data || err.message);
      });
  }, [open, initialUser?.id, form.organization, organization?.id]);




  /* ---------------------------------------------------
   * Initialize site memberships (edit mode)
   * --------------------------------------------------- */

  /* ---------------------------------------------------
   * Submit
   * --------------------------------------------------- */
  const handleSubmit = (e) => {
    e.preventDefault();

    let finalDefaultSiteId = defaultSiteId;
    if (!finalDefaultSiteId && selectedSites.length > 0) {
      finalDefaultSiteId = selectedSites[0];
    }

    const payload = {
      id: initialUser?.id,
      name: form.name,
      email: form.email,
      password: form.password || undefined,
      org_role: form.org_role,
      organization: form.organization,
      sites: selectedSites,
      default_site_id: finalDefaultSiteId,
    };


    console.error("✅ FINAL PAYLOAD", payload);
    onSubmit(payload);
  };

  /* ---------------------------------------------------
   * Guards
   * --------------------------------------------------- */
  if (!open) return null;

  if (!currentUser) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50 text-white">
        Loading…
      </div>
    );
  }

  /* ---------------------------------------------------
   * UI
   * --------------------------------------------------- */
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
      <div className="bg-neutral-900 rounded-2xl p-6 w-full max-w-md border border-gray-700 shadow-xl">
        <h2 className="text-xl font-semibold text-white mb-4">
          {mode === "edit" ? "Edit User" : "Add User"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-sm text-gray-300">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
              className="w-full border border-gray-600 rounded p-2 bg-neutral-800 text-white"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-sm text-gray-300">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
              className="w-full border border-gray-600 rounded p-2 bg-neutral-800 text-white"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-sm text-gray-300">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
              className="w-full border border-gray-600 rounded p-2 bg-neutral-800 text-white"
              placeholder={
                mode === "edit"
                  ? "Leave blank to keep current password"
                  : ""
              }
              required={mode === "add"}
            />
          </div>

          {/* Org Role */}
          {isOrgAdmin && (
            <div>
              <label className="text-sm text-gray-300">
                Organization Role
              </label>
              <select
                value={form.org_role}
                onChange={(e) =>
                  setForm({ ...form, org_role: e.target.value })
                }
                className="w-full border border-gray-600 rounded p-2 bg-neutral-800 text-white"
              >
                <option value="org_user">Org User</option>
                <option value="org_admin">Org Admin</option>
              </select>
            </div>
          )}

          {/* Organization selector */}
          {isPlatformAdmin && organizations.length > 0 && (
            <div>
              <label className="text-sm text-gray-300">
                Organization
              </label>
              <select
                value={form.organization}
                onChange={(e) =>
                  setForm({ ...form, organization: e.target.value })
                }
                className="w-full border border-gray-600 rounded p-2 bg-neutral-800 text-white"
              >
                <option value="">Select organization</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ---------------------------------------------------
              Site Access + Default
             --------------------------------------------------- */}
          {isOrgAdmin && form.organization && (
            <div>
              <label className="text-sm text-gray-300 mb-1 block">
                Site Access
              </label>

              <div className="border border-gray-700 rounded-lg bg-neutral-800 p-3 space-y-2 max-h-56 overflow-y-auto">
                {loadingSites && (
                  <div className="text-gray-400 text-sm">
                    Loading sites…
                  </div>
                )}

                {!loadingSites && sites.length === 0 && (
                  <div className="text-gray-400 text-sm">
                    No sites available.
                  </div>
                )}

                {!loadingSites &&
                  sites.map((site) => {
                    const checked = selectedSites.includes(site.id);

                    return (
                      <div
                        key={site.id}
                        className="flex items-center justify-between text-sm text-gray-200"
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSites((prev) => {
                                const next = [...prev, site.id];
                                if (!defaultSiteId) setDefaultSiteId(site.id);
                                return next;
                              });
                            } else {
                              setSelectedSites((prev) => {
                                const next = prev.filter((id) => id !== site.id);
                                if (defaultSiteId === site.id) {
                                  setDefaultSiteId(next[0] || null);
                                }
                                return next;
                              });
                            }
                          }}
                        />
                        {site.name}

                        </label>

                        {checked && (
                          <label className="flex items-center gap-1 text-xs text-gray-400">
                            <input
                              type="radio"
                              name="defaultSite"
                              checked={defaultSiteId === site.id}
                              onChange={() =>
                                setDefaultSiteId(site.id)
                              }
                            />
                            Default
                          </label>
                        )}
                      </div>
                    );
                  })}
              </div>

              <p className="text-xs text-gray-400 mt-1">
                Default site determines where the user lands on login.
              </p>
            </div>
          )}



{/* ---------------------------------------------------
    Org User: Default Site ONLY
--------------------------------------------------- */}
{isOrgUser && sites.length > 1 && (
  <div>
    <label className="text-sm text-gray-300 mb-1 block">
      Default Site
    </label>

    <div className="border border-gray-700 rounded-lg bg-neutral-800 p-3 space-y-2">
      {sites
        .filter((s) => selectedSites.includes(s.id))
        .map((site) => (
          <label
            key={site.id}
            className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer"
          >
            <input
              type="radio"
              name="defaultSite"
              checked={defaultSiteId === site.id}
              onChange={() => setDefaultSiteId(site.id)}
            />
            {site.name}
          </label>
        ))}
    </div>

    <p className="text-xs text-gray-400 mt-1">
      This controls where you land after login.
    </p>
  </div>
)}



          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
            >
              {mode === "edit" ? "Save Changes" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

